import type { ZodType } from "zod";
import z from "zod";

import {
  dayjs,
  ISO_DATE_FORMAT,
  IsoDateSchema,
  looseUuid,
  UK_TIME_ZONE,
  validateNhsNumber,
  type IsoDateUnbranded,
} from "@thalamos/common";

import {
  OrganisationIdSchema,
  type OrganisationIdUnbranded,
} from "./organisation.js";
import { UPN_SYSTEM, UpnSchema, type UpnUnbranded } from "./upn.js";

export type PersonIdUnbranded = string;
export const PersonIdSchema =
  looseUuid().brand<"haven-common:PersonId">() satisfies ZodType<PersonIdUnbranded>;
/**
 * A child or young person whose record Haven holds — the subject of an
 * assessment, never someone who signs in. Distinct from `UserId` so the two
 * kinds of person cannot be handed to each other's endpoints.
 */
export type PersonId = z.infer<typeof PersonIdSchema>;

export type PersonMergeIdUnbranded = string;
export const PersonMergeIdSchema =
  looseUuid().brand<"haven-common:PersonMergeId">() satisfies ZodType<PersonMergeIdUnbranded>;
/**
 * One merge of a young person's record into another: the row holding why, and
 * whether it has been undone. Branded apart from `PersonId` because a merge and
 * the two records it joins are all UUIDs, named side by side in the trail.
 */
export type PersonMergeId = z.infer<typeof PersonMergeIdSchema>;

/**
 * The width of the columns that hold name parts and identifier systems and
 * values; the schema refuses what the column would.
 */
const TEXT_MAX_LENGTH = 255;

/**
 * How a name is used, following FHIR's `HumanName.use` so a name pulled from
 * the Personal Demographics Service lands without translation.
 *
 * Written out as a union as well as a schema because tsoa follows the type into
 * the API contract but cannot follow `z.infer`. The column is a plain string;
 * the closed set lives here.
 */
export type PersonNameUse =
  "official" | "usual" | "nickname" | "old" | "maiden" | "temp" | "anonymous";

export const PersonNameUseSchema = z.enum([
  "official",
  "usual",
  "nickname",
  "old",
  "maiden",
  "temp",
  "anonymous",
]) satisfies ZodType<PersonNameUse>;

export type PersonNameUnbranded = {
  use: PersonNameUse;
  family: string | null;
  given: string[];
  periodStart: IsoDateUnbranded | null;
  periodEnd: IsoDateUnbranded | null;
};

const namePart = z
  .string()
  .trim()
  .min(1, "personPage.name.partEmpty")
  .max(TEXT_MAX_LENGTH, "personPage.name.partTooLong");

/** A bare calendar date, as the record stores it. */
const calendarDate = z.iso.date("personPage.date.invalid").pipe(IsoDateSchema);

export type DateOfBirthUnbranded = IsoDateUnbranded;
export const DateOfBirthSchema =
  calendarDate.brand<"haven-common:DateOfBirth">() satisfies ZodType<DateOfBirthUnbranded>;

/**
 * The day a young person was born. Branded apart from other calendar dates,
 * so another cannot be handed where a date of birth is meant; still an
 * `IsoDate`, so date helpers take it.
 *
 * The brand does not check it is no later than today: only a request stating
 * one does, because reading a stored one against a reader's clock, which may
 * run behind, could refuse a child recorded today.
 */
export type DateOfBirth = z.infer<typeof DateOfBirthSchema>;

/**
 * The form sends an empty field as an empty string; the record holds no date as
 * `null`. Read as one thing so the two halves agree on what "not given" is.
 */
const optionalDate = <Schema extends z.ZodType<string>>(date: Schema) =>
  z.preprocess((value) => (value === "" ? null : value), date.nullable());

const optionalCalendarDate = optionalDate(calendarDate);

// "Today" is London's calendar date, so a child born today is never refused.
// `abort` so a date that does not parse is not also called a future one.
const dateOfBirth = optionalDate(
  z.iso
    .date({ error: "personPage.date.invalid", abort: true })
    .refine(
      (date) => date <= dayjs().tz(UK_TIME_ZONE).format(ISO_DATE_FORMAT),
      "personPage.dateOfBirth.future",
    )
    .pipe(DateOfBirthSchema),
);

export const PersonNameSchema = z
  .object({
    use: PersonNameUseSchema,
    family: z.preprocess(
      (value) => (value === "" ? null : value),
      namePart.nullable(),
    ),
    given: z.array(namePart),
    periodStart: optionalCalendarDate,
    periodEnd: optionalCalendarDate,
  })
  .superRefine((name, context) => {
    if (name.family === null && name.given.length === 0) {
      context.addIssue({
        code: "custom",
        message: "personPage.name.required",
        path: ["family"],
      });
    }

    if (
      name.periodStart !== null &&
      name.periodEnd !== null &&
      name.periodEnd < name.periodStart
    ) {
      context.addIssue({
        code: "custom",
        message: "personPage.name.periodReversed",
        path: ["periodEnd"],
      });
    }
  })
  .brand<"haven-common:PersonName">() satisfies ZodType<PersonNameUnbranded>;

/**
 * One name a young person is, or was, known by.
 *
 * A set of names rather than a first-name/surname pair, because the domain has
 * more than one: the name on a birth certificate, the name a child asks to be
 * called, the name a previous placement recorded. Each carries a use and the
 * period it applied for, so the history of what someone was called is part of
 * the record rather than something to be reconstructed from a change log.
 *
 * `family` may be absent — a single-name person is not a validation failure —
 * but a name with neither a family nor a given part names nobody, and is
 * refused. The periods are bare calendar dates: a name is adopted from a day,
 * not from an instant.
 */
export type PersonName = z.infer<typeof PersonNameSchema>;

/** The identifier system the NHS number belongs to, as FHIR names it. */
export const NHS_NUMBER_SYSTEM = "https://fhir.nhs.uk/Id/nhs-number";

export type PersonIdentifierUnbranded = {
  system: string;
  value: string;
};

/**
 * NHS numbers are commonly written with spaces (`943 476 5919`); the stored
 * value is the ten digits, so the same number cannot be recorded twice under
 * two spellings.
 */
const withoutNhsNumberSpacing = (raw: unknown): unknown =>
  typeof raw === "object" &&
  raw !== null &&
  "system" in raw &&
  raw.system === NHS_NUMBER_SYSTEM &&
  "value" in raw &&
  typeof raw.value === "string"
    ? { ...raw, value: raw.value.replaceAll(/\s/g, "") }
    : raw;

export const PersonIdentifierSchema = z.preprocess(
  withoutNhsNumberSpacing,
  z
    .object({
      system: z
        .url("personPage.identifier.systemInvalid")
        .max(TEXT_MAX_LENGTH, "personPage.identifier.systemTooLong"),
      value: z
        .string()
        .trim()
        .min(1, "personPage.identifier.valueRequired")
        .max(TEXT_MAX_LENGTH, "personPage.identifier.valueTooLong"),
    })
    .superRefine((identifier, context) => {
      if (
        identifier.system === NHS_NUMBER_SYSTEM &&
        !validateNhsNumber(identifier.value)
      ) {
        context.addIssue({
          code: "custom",
          message: "personPage.identifier.nhsNumberInvalid",
          path: ["value"],
        });
      }
    })
    .transform((identifier, context) => {
      if (identifier.system !== UPN_SYSTEM) {
        return identifier;
      }

      const upn = UpnSchema.safeParse(identifier.value);

      if (!upn.success) {
        for (const issue of upn.error.issues) {
          context.addIssue({
            code: "custom",
            message: issue.message,
            path: ["value"],
          });
        }

        return z.NEVER;
      }

      return { ...identifier, value: upn.data };
    })
    .brand<"haven-common:PersonIdentifier">(),
) satisfies ZodType<PersonIdentifierUnbranded>;

/**
 * An identifier another system knows this person by: an NHS number, a case
 * reference in a local authority's own system.
 *
 * FHIR's `Identifier` shape — a system URI and a value — so an identifier
 * arriving from PDS or a partner's API is stored as it was received. The
 * system is any URI rather than a closed set, because the third-party systems
 * Haven will link to are not yet known, and each one's identifiers are only
 * meaningful alongside the URI that says whose they are.
 */
export type PersonIdentifier = z.infer<typeof PersonIdentifierSchema>;

export type HomeAddressUnbranded = {
  address: string;
  postcode: string;
};

export const HomeAddressSchema = z
  .object({
    address: z
      .string()
      .trim()
      .min(1, "personPage.homeAddress.addressRequired")
      .max(TEXT_MAX_LENGTH, "personPage.homeAddress.addressTooLong"),
    postcode: z
      .string()
      .trim()
      .min(1, "personPage.homeAddress.postcodeRequired")
      .max(8, "personPage.homeAddress.postcodeTooLong"),
  })
  .brand<"haven-common:HomeAddress">() satisfies ZodType<HomeAddressUnbranded>;

/**
 * Where a young person lives. Held as eMHA holds it — one free-text address and
 * a postcode, each limited only in length — so a record carried across does not
 * have to be reshaped.
 */
export type HomeAddress = z.infer<typeof HomeAddressSchema>;

/**
 * One record of a young person, as it was registered: the fields the record
 * holds for itself, whether or not it has since been merged into another.
 *
 * Who may reach it is decided by the involvements recorded about the young
 * person — see
 * `docs/decisions/2026-09-12-a-young-persons-reach-is-the-relationships-recorded-about-them.md`.
 */
export type PersonRecordUnbranded = {
  id: PersonIdUnbranded;
  createdByOrganisationId: OrganisationIdUnbranded;
  dateOfBirth: DateOfBirthUnbranded | null;
  names: PersonNameUnbranded[];
  identifiers: PersonIdentifierUnbranded[];
};

/**
 * Deliberately unbranded, like `UserSchema`: a `Person` is the wider view of a
 * record, and a brand here would stop one being passed where a record is enough.
 */
export const PersonRecordSchema = z.object({
  id: PersonIdSchema,
  createdByOrganisationId: OrganisationIdSchema,
  dateOfBirth: DateOfBirthSchema.nullable(),
  names: z.array(PersonNameSchema),
  identifiers: z.array(PersonIdentifierSchema),
}) satisfies ZodType<PersonRecordUnbranded>;

export type PersonRecord = z.infer<typeof PersonRecordSchema>;

/**
 * A record that has been merged into another, carried with the record it was
 * merged into so a reader can see the tree rather than only its root.
 *
 * Its own fields are kept as they were registered — nothing is copied onto the
 * surviving record — so unmerging gives back exactly what was there.
 */
export type MergedPersonRecordUnbranded = PersonRecordUnbranded & {
  mergedIntoPersonId: PersonIdUnbranded;
  mergedAt: Date;
  mergeReason: string;
};

/**
 * `mergeReason` is carried here rather than in the audit trail: it is prose
 * somebody typed about a child, so it is readable by whoever may reach the
 * record and by nobody else. See
 * `docs/decisions/2026-09-10-the-trail-records-the-act-and-the-record-holds-the-reason.md`.
 */
export const MergedPersonRecordSchema = PersonRecordSchema.extend({
  mergedIntoPersonId: PersonIdSchema,
  mergedAt: z.coerce.date(),
  mergeReason: z.string(),
}) satisfies ZodType<MergedPersonRecordUnbranded>;

export type MergedPersonRecord = z.infer<typeof MergedPersonRecordSchema>;

/**
 * A young person as the API returns them: the surviving record and every
 * record merged into it.
 *
 * Always the surviving record. Asking for a record that has been merged away
 * answers with the record it now belongs to, whose `id` differs from the one
 * asked for — which is how a caller learns the merge happened.
 */
export type PersonUnbranded = PersonRecordUnbranded & {
  mergedPersonRecords: MergedPersonRecordUnbranded[];
  /**
   * The surviving record's, never one merged into it. Carried on the single
   * record and not on `PersonRecord`, so a listing never sends where a child
   * lives.
   */
  homeAddress: HomeAddressUnbranded | null;
  /** When the surviving record was archived, or null while it is not. */
  archivedAt: Date | null;
};

/**
 * Branded, so holding a `Person` means one was parsed rather than assembled from
 * rows that merely looked right.
 */
export const PersonSchema = PersonRecordSchema.extend({
  mergedPersonRecords: z.array(MergedPersonRecordSchema),
  homeAddress: HomeAddressSchema.nullable(),
  archivedAt: z.coerce.date().nullable(),
}).brand<"haven-common:Person">() satisfies ZodType<PersonUnbranded>;

export type Person = z.infer<typeof PersonSchema>;

/**
 * The name to show for a set of names: the current official one, else the
 * current usual one, else any current one, else the first.
 *
 * A name is current while it has no period end. The order follows what the
 * record is for — an assessment is about a child under the name their
 * documents carry — while still showing *something* for a record whose only
 * name is a nickname.
 */
export const preferredName = (
  names: readonly PersonNameUnbranded[],
): PersonNameUnbranded | undefined => {
  const current = names.filter((name) => name.periodEnd === null);

  return (
    current.find((name) => name.use === "official") ??
    current.find((name) => name.use === "usual") ??
    current[0] ??
    names[0]
  );
};

/** A name as one line: given names, then the family name. */
export const formatPersonName = (name: PersonNameUnbranded): string =>
  [...name.given, ...(name.family === null ? [] : [name.family])].join(" ");

/**
 * How a record is labelled in a listing or a heading. Empty only for a record
 * with no names, which the request schemas refuse.
 */
export const displayName = (names: readonly PersonNameUnbranded[]): string => {
  const name = preferredName(names);

  return name === undefined ? "" : formatPersonName(name);
};

/**
 * What a request states about a young person themselves: what they are called
 * and when they were born.
 *
 * Identifiers are not here. An identifier says which person some other system's
 * record is about, so changing one re-points the record at a different child
 * while leaving everything attached to it in place — which is a merge's kind of
 * consequence with none of a merge's ceremony. They are stated once, when the
 * record is registered, and afterwards only a link to the system that issued
 * them may move one.
 */
export type PersonDetailsUnbranded = {
  dateOfBirth: DateOfBirthUnbranded | null;
  names: PersonNameUnbranded[];
};

/**
 * What registering a young person states: their details, and the identifiers the
 * record starts life holding.
 *
 * The identifiers are the whole set the record will hold, not a delta, so a
 * record cannot end up holding one that was never in the body that made it.
 */
export type CreatePersonRequestUnbranded = PersonDetailsUnbranded & {
  identifiers: PersonIdentifierUnbranded[];
  /** Where the young person lives, or `null` when that is not known. */
  homeAddress: HomeAddressUnbranded | null;
};

const PersonDetailsSchema = z.object({
  dateOfBirth,
  names: z.array(PersonNameSchema).min(1, "personPage.names.required"),
}) satisfies ZodType<PersonDetailsUnbranded>;

export type PersonDetails = z.infer<typeof PersonDetailsSchema>;

export const CreatePersonRequestSchema = PersonDetailsSchema.extend({
  // A correction may leave a name with only one part, as a single-name person
  // has; a registration states both, so the record starts out fully named.
  names: PersonDetailsSchema.shape.names.refine(
    (names) =>
      names.some((name) => name.family !== null && name.given.length > 0),
    "personPage.names.forenameAndSurname",
  ),
  identifiers: z
    .array(PersonIdentifierSchema)
    .superRefine((identifiers, context) => {
      // One value per system within a record. The database refuses the same
      // identifier on two records; this is the same rule within one, said here so
      // the caller is told which entry is the repeat.
      const seen = new Set<string>();

      identifiers.forEach((identifier, index) => {
        const key = `${identifier.system}|${identifier.value}`;

        if (seen.has(key)) {
          context.addIssue({
            code: "custom",
            message: "personPage.identifier.duplicate",
            path: [index, "value"],
          });
        }

        seen.add(key);
      });
    }),
  homeAddress: HomeAddressSchema.nullable(),
}).brand<"haven-common:CreatePersonRequest">() satisfies ZodType<CreatePersonRequestUnbranded>;

export type CreatePersonRequest = z.infer<typeof CreatePersonRequestSchema>;

export const UpdatePersonRequestSchema =
  PersonDetailsSchema.brand<"haven-common:UpdatePersonRequest">() satisfies ZodType<PersonDetailsUnbranded>;

export type UpdatePersonRequest = z.infer<typeof UpdatePersonRequestSchema>;

/**
 * Why two records are being treated as one person, or why they no longer are.
 *
 * Required, because a merge is the most consequential thing that can be done to
 * a record short of deleting it, and the reason is what anybody looking at the
 * merge reads first. It is kept on the merge itself rather than in the audit
 * trail, because it is prose about a child: see
 * [[MergedPersonRecordSchema]] and
 * `docs/decisions/2026-09-10-the-trail-records-the-act-and-the-record-holds-the-reason.md`.
 */
const MergeReasonSchema = z
  .string()
  .trim()
  .min(1, "personPage.merge.reasonRequired")
  .max(1000, "personPage.merge.reasonTooLong");

/**
 * Asks for one record to be merged into the record the request is addressed
 * to. The record named here stops being shown on its own; the one addressed
 * survives.
 */
export type MergePersonRequestUnbranded = {
  sourcePersonId: PersonIdUnbranded;
  reason: string;
};

export const MergePersonRequestSchema = z
  .object({
    sourcePersonId: PersonIdSchema,
    reason: MergeReasonSchema,
  })
  .brand<"haven-common:MergePersonRequest">() satisfies ZodType<MergePersonRequestUnbranded>;

export type MergePersonRequest = z.infer<typeof MergePersonRequestSchema>;

export type UnmergePersonRequestUnbranded = {
  reason: string;
};

export const UnmergePersonRequestSchema = z
  .object({
    reason: MergeReasonSchema,
  })
  .brand<"haven-common:UnmergePersonRequest">() satisfies ZodType<UnmergePersonRequestUnbranded>;

export type UnmergePersonRequest = z.infer<typeof UnmergePersonRequestSchema>;

/**
 * The most people one match offers. Enough to choose between; few enough that a
 * searcher is asked to narrow the search rather than read down a list of other
 * people's children.
 */
export const PERSON_MATCH_LIMIT = 10;

/**
 * What somebody searching for an existing record before registering one knows:
 * a surname, perhaps a forename and a date of birth, or a UPN.
 */
export type PersonMatchRequestUnbranded =
  | {
      type: "demographics";
      family: string;
      given?: string | undefined;
      phoneticFamily: boolean;
      phoneticGiven: boolean;
      dateOfBirth: DateOfBirthUnbranded | null;
    }
  | {
      type: "upn";
      upn: UpnUnbranded;
    };

export type DemographicMatchRequestUnbranded = Extract<
  PersonMatchRequestUnbranded,
  { type: "demographics" }
>;

export const PersonMatchRequestSchema = z
  .discriminatedUnion("type", [
    z.object({
      type: z.literal("demographics"),
      family: z
        .string()
        .trim()
        .min(1, "personMatch.family.required")
        .max(TEXT_MAX_LENGTH, "personPage.name.partTooLong"),
      given: z.preprocess(
        (value) => (value === "" ? undefined : value),
        z
          .string()
          .trim()
          .max(TEXT_MAX_LENGTH, "personPage.name.partTooLong")
          .optional(),
      ),
      phoneticFamily: z.boolean(),
      phoneticGiven: z.boolean(),
      dateOfBirth,
    }),
    z.object({
      type: z.literal("upn"),
      upn: UpnSchema,
    }),
  ])
  .brand<"haven-common:PersonMatchRequest">() satisfies ZodType<PersonMatchRequestUnbranded>;

export type PersonMatchRequest = z.infer<typeof PersonMatchRequestSchema>;

export type DemographicMatchRequest = Extract<
  PersonMatchRequest,
  { type: "demographics" }
>;

export type PersonMatchUnbranded = {
  id: PersonIdUnbranded;
  names: PersonNameUnbranded[];
  dateOfBirth: DateOfBirthUnbranded | null;
};

export type PersonMatchesUnbranded =
  | { searchOutcome: "success"; persons: PersonMatchUnbranded[] }
  | { searchOutcome: "too-many-matches" };

export const PersonMatchesSchema = z.discriminatedUnion("searchOutcome", [
  z.object({
    searchOutcome: z.literal("success"),
    persons: z.array(
      z.object({
        id: PersonIdSchema,
        names: z.array(PersonNameSchema),
        dateOfBirth: DateOfBirthSchema.nullable(),
      }),
    ),
  }),
  z.object({ searchOutcome: z.literal("too-many-matches") }),
]) satisfies ZodType<PersonMatchesUnbranded>;

export type PersonMatches = z.infer<typeof PersonMatchesSchema>;
